import { useCallback, useMemo } from 'react';

import useEffectOnce from 'hooks/use-effect-once';
import useReadyQueue from 'hooks/use-ready-queue';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { normalizeBooleans } from 'state/mParticle/utils';
import { isRecentCookieTimestamp } from 'utils/cookies';
import logger from 'utils/logger';

interface IBranchPurchaseTransaction {
  transactionId?: string;
  currency?: string;
  revenue?: number;
  shipping?: number;
  tax?: number;
  serviceMode?: string;
}

export interface IBranchContext {
  initBranch: () => void;
  logPurchase(tnx: IBranchPurchaseTransaction): void;
}

const useBranch = () => {
  const { enqueueIfNotDrained, drainQueue } = useReadyQueue();
  const enableCookieBanner = useFlag(LaunchDarklyFlag.ENABLE_COOKIE_BANNER);
  const hasAcceptedCookies = isRecentCookieTimestamp();

  const logPurchase = useCallback(
    enqueueIfNotDrained((tnx: IBranchPurchaseTransaction) => {
      // Be defensive here:
      // branch should be available, but we don't to break if it's not
      // e.g. an ad blocker has blocked it.
      if (window.branch && window.branch.logEvent) {
        // If we the serviceMode is `None` or undefined return an empty string
        const customerEventAlias = !tnx.serviceMode;
        const normalizedAttrs = normalizeBooleans({
          transaction_id: tnx.transactionId,
          currency: tnx.currency,
          revenue: tnx.revenue,
          shipping: tnx.shipping,
          tax: tnx.tax,
        });
        window.branch.logEvent('PURCHASE', normalizedAttrs, null, customerEventAlias, function (
          err: Error
        ) {
          if (err) {
            logger.error({ message: 'Branch: Error creating Purchase event' });
          }
        });
      }
    }),
    [enqueueIfNotDrained]
  );

  const initBranch = useCallback(() => {
    import('../init')
      .then(({ default: init }) => {
        init(drainQueue);
      })
      .catch(error => {
        logger.error({ error, message: 'Branch: import error' });
      });
  }, [drainQueue]);

  useEffectOnce(() => {
    if (!enableCookieBanner || hasAcceptedCookies) {
      initBranch();
    }
  });

  const ctxValue: IBranchContext = useMemo(
    () => ({
      initBranch,
      logPurchase,
    }),
    [initBranch, logPurchase]
  );

  return ctxValue;
};

export default useBranch;

declare global {
  interface Window {
    branch: any;
  }
}
